/**
 * 業務フロー（Flow）の入力スキーマ（NE §1-attrs の Flow / API §3.4b）。今の UserStory の後継。
 *
 * 節点が業務を**直に**指す。今は `type: "process"` の節点が `Operation` を指し、そこから業務へ
 * 2 ホップで辿っていた。分かれ道を表していた `decision` 節点は無くなり、前の業務の結末の行と
 * 枝の宛先の型で表す。型（主 / 入れる / 出す / 割り込み）を宣言し、型ごとに入口と出口を持つ。
 *
 * **受け渡し（送り手, 受け手, 渡すもの）の欄は持たない**（決定。2026-09-21）。フローは複数の
 * レーンを前提にした図で、レーンをまたぐ枝がそのまま受け渡しを表している。何が渡るかは前の
 * 業務の出力と次の業務の入力から読める。欄を足すと同じ事実の 2 つ目の表現になる。
 *
 * **レーンは入力に持たない。** 節点の業務のアクターの和で導出できる。入力で受けると、導出と
 * 食い違う 2 つ目の値を書けてしまう。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 業務フロー（Flow）の zod 入力スキーマ。型ごとに入口と出口を持ち、節点が業務を直に指し、枝が宛先の型を持つ
 * @graph-connects none
 */
import { z } from 'zod';
import { localElementId, optionalSpecElementId, specElementId } from './ids.js';
import { restoredLabelSchema } from './masters.js';
/**
 * フローの節点の種別 6 種。
 *
 * 今の `process` は `business` に替わり、`decision` は消える。並列（`parallelSplit` /
 * `parallelJoin`）は、1 本の枝の並びでは書けない形を表すために増えた。任意順の束は持たない
 * （2026-09-26 の決定）。順序を問わない業務は、並列の分岐と合流で表す。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business フローの節点の種別（業務 / 開始 / 終了 / 並列の分岐 / 並列の合流）の allowlist
 * @graph-connects none
 */
export const FLOW_NODE_KINDS = [
    'business',
    'start',
    'end',
    'parallelSplit',
    'parallelJoin',
];
/**
 * 業務フローの型 4 種（主 / 入れる / 出す / 割り込み）。業務フローの判別ユニオンの鍵。
 *
 * 型で入口と出口の形が決まる。役割を導出にすると「入口・出口が無い」を書込で止められず、
 * 型ごとに違う必須を検査できないので、書き手が宣言する。宣言と値の流れが合うかは制約 63 が見る。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 業務フローの型（主 / 入れる / 出す / 割り込み）の allowlist
 * @graph-connects none
 */
export const FLOW_TYPES = ['main', 'inbound', 'outbound', 'interrupt'];
/**
 * 主フローの入口の種別 2 種（前のステージの出口 / 鎖の始まり）。`mainFlowEntrySchema` の
 * 判別の鍵と同じ並び。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 主フローの入口の種別（previousStage / streamStart）の allowlist
 * @graph-connects none
 */
export const FLOW_MAIN_ENTRY_KINDS = ['previousStage', 'streamStart'];
/**
 * 割り込み型の出口の種別 2 種（中止 / 前のステージの入口へ戻す）。`interruptFlowExitSchema` の
 * 判別の鍵と同じ並び。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 割り込み型の業務フローの出口の種別（abort / previousStage）の allowlist
 * @graph-connects none
 */
export const FLOW_INTERRUPT_EXIT_KINDS = ['abort', 'previousStage'];
/**
 * 支援フロー（入れる / 出す / 割り込み）の引き金 4 種（時間 / ロールの操作 / 主フローの結末 /
 * 外部の出来事）。`supportFlowEntrySchema` の判別の鍵と同じ並び。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 支援フローの入口の引き金（timer / operation / ending / external）の allowlist
 * @graph-connects none
 */
export const FLOW_TRIGGER_KINDS = ['timer', 'operation', 'ending', 'external'];
/**
 * 出す型の出口の終端 3 種（通知 / 記録 / 外部へ送る）。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 出す型の業務フローの出口の終端（notify / record / send）の allowlist
 * @graph-connects none
 */
export const FLOW_OUTBOUND_EFFECTS = ['notify', 'record', 'send'];
/**
 * 枝の宛先の型 4 種（次へ / 戻る / 止まる / 出口へ）。
 *
 * 枝が持つのはフローの中の行き先だけである。渡す先・前のステージ・中止はフロー全体の性質
 * なので、フローの出口（`exit`）が持つ。業務の結末も行き先を持たない — 同じ業務が複数の
 * フローから使われ、行き先はフローごとに違うためである。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 枝の宛先の型（次へ / 戻る / 止まる / 出口へ）の allowlist
 * @graph-connects none
 */
export const FLOW_DESTINATION_KINDS = ['next', 'back', 'stop', 'exit'];
/**
 * 同じフローの節点（`to`）を必ず持つ宛先の型。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 枝の宛先の型のうち、行き先の節点（to）を必須にする型（next / back）
 * @graph-connects none
 */
export const FLOW_DESTINATION_KINDS_WITH_TO = [
    'next',
    'back',
];
/**
 * 業務を指す節点。`id` はフローの中で 1 から振る番号である。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 業務を指すフロー節点の入力スキーマ
 * @graph-connects none
 */
export const businessFlowNodeSchema = z.strictObject({
    id: localElementId(),
    kind: z.literal('business'),
    business: specElementId(),
});
/**
 * 番号のほかに欄を持たない節点（開始・終了・並列の分岐・並列の合流）。業務を指さないので
 * `business` は書けない。
 *
 * @param kind 節点の種別
 * @returns その節点の zod 入力スキーマ
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 欄を持たないフロー節点（開始 / 終了 / 並列の分岐 / 並列の合流）の zod 入力スキーマを組み立てる
 * @graph-connects none
 */
function bareFlowNodeSchema(kind) {
    return z.strictObject({
        id: localElementId(),
        kind: z.literal(kind),
    });
}
/**
 * フローの節点。種別で枝を選ぶ判別ユニオン 5 枝。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business フローの節点の zod 入力スキーマ（種別 5 種の判別ユニオン）
 * @graph-connects none
 */
export const flowNodeElementSchema = z.discriminatedUnion('kind', [
    businessFlowNodeSchema,
    bareFlowNodeSchema('start'),
    bareFlowNodeSchema('end'),
    bareFlowNodeSchema('parallelSplit'),
    bareFlowNodeSchema('parallelJoin'),
]);
/**
 * フローの枝（前の節点, 結末の行, 宛先の型, 次の節点）。
 *
 * `ending` は `from` の業務の結末の行を、その行の結果の値（用語の id）で指す。結末の行は局所 id を
 * 持たず、結果の値が行の鍵だからである。`from` が業務でない節点（開始・合流）の枝には値が
 * 入らない。
 *
 * `to` は next / back で必須にする。止まる・出口への枝も `to` を書けるのは、終了の節点を指して
 * 図の終わりを描く書き方を受けるためである。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business フローの枝（前の節点・結末の行・宛先の型・次の節点）の入力スキーマ
 * @graph-connects none
 */
export const flowTransitionElementSchema = z
    .strictObject({
    from: localElementId(),
    ending: specElementId().optional(),
    destinationKind: z.enum(FLOW_DESTINATION_KINDS),
    to: localElementId().optional(),
})
    .refine((transition) => transition.to !== undefined ||
    !FLOW_DESTINATION_KINDS_WITH_TO.includes(transition.destinationKind), {
    message: `宛先の型が ${FLOW_DESTINATION_KINDS_WITH_TO.join(' / ')} の枝は to（同じフローの節点）を持つ`,
    path: ['to'],
});
/**
 * 業務の結末の行への参照（業務 1 件と、その結末の行の結果の値）。ステージの出口と同じ指し方を
 * する（`stageExitSchema`）。
 *
 * @graph-connects none
 */
const endingRowShape = {
    business: specElementId(),
    ending: specElementId(),
};
/**
 * 支援フロー（入れる / 出す / 割り込み）の出口に着く結末の行。1 行以上。
 *
 * そのフローの業務の節点が指す業務の、結末の行を指す。入れる型なら値を渡すと決まる結末、出す型
 * なら外へ出したと決まる結末、割り込み型なら中止・戻すと決まる結末である。主フローはステージの
 * 出口の結末の行（`exit` の `business`・`ending`）がこれに当たるので持たない。「出口へ」の枝が
 * 運ぶ結末の行がここに在るか、ここに在る行から出る枝が「出口へ」かは制約 53 が見る。
 *
 * @graph-connects none
 */
const exitEndingsShape = {
    endings: z.array(z.strictObject(endingRowShape)).min(1),
};
/**
 * 主フローの入口。前のステージの出口（結末の行）か、鎖の始まり（最初のステージ）。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 主フローの入口（前のステージの出口の結末の行 / 鎖の始まり）の入力スキーマ
 * @graph-connects none
 */
export const mainFlowEntrySchema = z.discriminatedUnion('kind', [
    z.strictObject({ kind: z.literal('previousStage'), ...endingRowShape }),
    z.strictObject({ kind: z.literal('streamStart') }),
]);
/**
 * 支援フロー（入れる / 出す / 割り込み）の入口。引き金の種別と、その参照。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 支援フローの入口（時間の用語 / 操作するアクター / 主フローの結末の行 / 外部の出来事の用語）の入力スキーマ
 * @graph-connects none
 */
export const supportFlowEntrySchema = z.discriminatedUnion('kind', [
    z.strictObject({ kind: z.literal('timer'), term: specElementId() }),
    z.strictObject({ kind: z.literal('operation'), actor: specElementId() }),
    z.strictObject({ kind: z.literal('ending'), ...endingRowShape }),
    z.strictObject({ kind: z.literal('external'), term: specElementId() }),
]);
/**
 * 割り込み型の出口。中止か、前のステージの入口へ戻す。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 割り込み型の業務フローの出口（中止 / 前のステージの入口へ戻す）の入力スキーマ
 * @graph-connects none
 */
export const interruptFlowExitSchema = z.discriminatedUnion('kind', [
    z.strictObject({ kind: z.literal('abort'), ...exitEndingsShape }),
    z.strictObject({ kind: z.literal('previousStage'), stage: specElementId(), ...exitEndingsShape }),
]);
/**
 * 型によらず業務フローが持つ欄。
 *
 * `transitions` は空でも受理する。復元は分かれ道の節点に触る枝を張り直せないまま落とすので、
 * 空の状態を保存できないと復元そのものが書けない。張り直しが済んだかは制約 59 が見る。
 *
 * `restoredLabel` は移行の間だけ持つ欄である（宣言は `masters.ts`）。
 *
 * @graph-connects none
 */
const flowCommonShape = {
    id: optionalSpecElementId(),
    stage: specElementId(),
    nodes: z.array(flowNodeElementSchema).min(1),
    transitions: z.array(flowTransitionElementSchema),
    restoredLabel: restoredLabelSchema.optional(),
};
/**
 * 業務フロー（Flow）。型（`type`）で入口と出口の形が決まる判別ユニオン 4 枝。
 *
 * ラベルと説明は持たない。ラベルは「ステージ ／ 状況」で導出する（`labels.ts`）。`situation`
 * は支援 3 型だけが持ち、主フローには書けない。
 *
 * 出口の相手が型と合うか（主の出口がステージの出口と一致する等）はグラフ全体を見ないと
 * 決まらないので、ここでは形だけを見る（制約 68）。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 業務フロー（Flow）の zod 入力スキーマ。型ごとの入口・出口・状況と、ステージへの帰属・節点・枝
 * @graph-connects none
 */
export const flowElementSchema = z.discriminatedUnion('type', [
    z.strictObject({
        ...flowCommonShape,
        type: z.literal('main'),
        entry: mainFlowEntrySchema,
        exit: z.strictObject(endingRowShape),
    }),
    z.strictObject({
        ...flowCommonShape,
        type: z.literal('inbound'),
        situation: specElementId(),
        entry: supportFlowEntrySchema,
        exit: z.strictObject({ business: specElementId(), ...exitEndingsShape }),
    }),
    z.strictObject({
        ...flowCommonShape,
        type: z.literal('outbound'),
        situation: specElementId(),
        entry: supportFlowEntrySchema,
        exit: z.strictObject({
            effect: z.enum(FLOW_OUTBOUND_EFFECTS),
            counterpart: specElementId(),
            ...exitEndingsShape,
        }),
    }),
    z.strictObject({
        ...flowCommonShape,
        type: z.literal('interrupt'),
        situation: specElementId(),
        entry: supportFlowEntrySchema,
        exit: interruptFlowExitSchema,
    }),
]);
//# sourceMappingURL=flow.js.map